import Button from 'components/Button/Button'
import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntersectionObserver } from 'usehooks-ts'
import { Icon } from '../Icon'

export default ({
  dialectKey,
  title,
  userAnswers,
  index,
  possibleDialectValues,
  setUserAnswers,
  isEnabled = false,
  ExampleSentence,
  titleRef,
  nextTitleElement,
  headerHeight = 0,
}: {
  dialectKey: string
  title: string
  imageSrc?: string
  userAnswers?: { title: string; value: string }[]
  index: number
  possibleDialectValues: { [key: string]: { title: string; value: string }[] }
  setUserAnswers: (userAnswers: { title: string; value: string }[]) => void
  isEnabled?: boolean
  ExampleSentence?: FC<{ input?: string }>
  titleRef?:
    | ((instance: HTMLHeadingElement | null) => void)
    | React.RefObject<HTMLHeadingElement>
    | null
    | undefined
  nextTitleElement?: any
  headerHeight?: number
}) => {
  const somethingIsSelected = userAnswers?.[index] !== undefined
  const ref = useRef<HTMLDivElement | null>(null)
  const entry = useIntersectionObserver(ref, {})
  const [isVisible, setIsVisible] = useState<boolean>(false)
  useEffect(() => {
    !!entry?.isIntersecting && setIsVisible(!!entry?.isIntersecting)
  }, [entry])

  const selectedWord = userAnswers?.[index]
  const scrollToNextTitle = () => {
    // we maybe have a sticky header, so we need to scroll a bit more
    var elementPosition = nextTitleElement.getBoundingClientRect().top
    var offsetPosition = elementPosition + window.scrollY - headerHeight
    setTimeout(() => {
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }, 500)
  }
  return (
    <WordSectionWrapper isVisible={isVisible} isEnabled={isEnabled} ref={ref}>
      <WordSectionTitle ref={titleRef}>{title}</WordSectionTitle>{' '}
      <Icon isActive={somethingIsSelected} name={title} />
      {ExampleSentence && (
        <ExampleSentenceWrapper>
          <ExampleSentence input={selectedWord?.title} />
        </ExampleSentenceWrapper>
      )}
      <WordButtonsWrapper somethingIsSelected={somethingIsSelected}>
        {possibleDialectValues[dialectKey].map((word) => {
          return (
            <Button
              disabled={!isEnabled}
              key={dialectKey + word.title}
              isOther={word.title === 'andere'}
              onClick={(event) => {
                const userAnswersCopy = [...(userAnswers ?? [])]
                userAnswersCopy[index] = word
                setUserAnswers(userAnswersCopy)
                scrollToNextTitle()
              }}
              className={
                selectedWord?.title === word.title ? 'selected' : 'not-selected'
              }
            >
              {word.title}
            </Button>
          )
        })}
      </WordButtonsWrapper>
    </WordSectionWrapper>
  )
}
const WordSectionWrapper = styled.section<{
  isVisible?: boolean
  isEnabled?: boolean
}>`
  display: flex;
  flex-direction: column;
  margin-bottom: 8rem;
  ${({ isEnabled }) =>
    !isEnabled &&
    css`
      opacity: 0.2;
    `}
  img {
    transform: ${({ isVisible }) =>
      isVisible ? 'scale(1) rotate(0)' : 'scale(.5) rotate(10deg)'};
  }
`
const ExampleSentenceWrapper = styled.div`
  font-size: 2.4rem;
  margin-bottom: 2rem;
  margin-top: 1rem;
  text-align: center;
`
const WordSectionTitle = styled.h5`
  font-family: var(--font-plex);
  font-size: 6.4rem;
  font-weight: 700;
  line-height: 1;
  @media screen and (max-width: 768px) {
    font-size: 3.5rem;
    white-space: pre-wrap;
  }
  text-align: center;
  margin: 0;

  &:first-letter {
    text-transform: uppercase;
  }
`

const WordButtonsWrapper = styled.div<{ somethingIsSelected?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  gap: 10px;
  padding: 10px;

  ${({ somethingIsSelected }) =>
    somethingIsSelected &&
    css`
      .not-selected {
        opacity: 0.8;
      }
    `}
`


